<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import {useAuthStore} from "~/plugins/store/auth";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";
import {recaptcha} from "~/composables/recaptcha";
import {useNuxtApp} from "#app";
import {useUserStore} from "~/plugins/store/user";
import {useReCaptcha} from "vue-recaptcha-v3";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import AccountLayout from "~/layouts/AccountLayout.vue";
import ConfirmationDialog from "~/components/utils/ConfirmationDialog.vue";
import {reloadNuxtApp} from "nuxt/app";
import {
  createDefaultUserPreference,
  type UserPreference,
} from "~/types/User";

export default defineComponent({
  components: {ConfirmationDialog, AccountLayout, LoadingDialog},
  setup() {
    definePageMeta({
      middleware: [
        'auth',
      ],
    });

    useHead({
      title: 'Xiga | Preferințe',
      meta: [
        {
          name: 'description',
          content: 'Accesează și gestionează preferințele. Aici poți seta notificări pentru RCA, asigurare de călătorie și asigurare locuință.'
        }
      ],
    });
    // Utility function to introduce a delay
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({}); // Initialize translations as a reactive reference
    const authStore = useAuthStore();
    const user = ref(authStore.getUser());
    let accessToken = authStore.getAccessToken();
    const userPreferenceForm = ref<UserPreference>(createDefaultUserPreference());
    const {$toast} = useNuxtApp();
    const userStore = useUserStore();
    const recaptchaInstance = useReCaptcha();
    const showLoadingDialog = ref(true);
    const messageLoadingDialog = ref('Se incarcă...');
    const showConfirmationDialog = ref(false);
    const messageConfirmationDialog = ref('');
    const messageContextDialog = ref('');
    const yesButtonText = ref('Da');
    const noButtonText = ref('Nu');

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      showLoadingDialog.value = true;
      messageLoadingDialog.value = 'Se încarcă...';
      if (accessToken) {
        const preferences = await userStore.getUserPreference(accessToken);
        if (preferences) {
          userPreferenceForm.value = preferences;
        }
      }
      showLoadingDialog.value = false;
    });


    const handleValidation = (field: string, status: boolean) => {
      if (!status) {
        console.log(`Validation failed for field: ${field}`);
        window.dispatchEvent(new CustomEvent('validate-customer', {detail: field}));
        return false;
      }
      return true;
    };

    const submitUserPreference = async () => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.customer_details_validating, 'Validăm datele introduse...');
      if (!user.value || !accessToken) {
        showLoadingDialog.value = false;
        $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
        return;
      }

      const token = await recaptcha(recaptchaInstance);
      userStore.updateUserPreference(userPreferenceForm.value, user.value.id, accessToken, token)
          .then(() => {
            showLoadingDialog.value = false;
            $toast.success(translation(translationList.value.customer_details_updated, 'Preferințele contului au fost actualizate cu succes!'));
          })
          .catch((error) => {
            showLoadingDialog.value = false;
            $toast.error(translation(translationList.value.customer_details_update_failed, 'Actualizarea preferințelor contului a eșuat!'));
            console.error(error);
          });
    };

    return {
      user,
      accessToken,
      translationList,
      translation,
      userPreferenceForm,
      submitUserPreference,
      showLoadingDialog,
      messageLoadingDialog,
      showConfirmationDialog,
      messageContextDialog,
      messageConfirmationDialog,
      yesButtonText,
      noButtonText,
    }
  }
});
</script>

<template>
  <account-layout class="profile">
    <v-card rounded="xl" variant="elevated" elevation="15" class="w-100 pa-5" min-height="400">
      <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
      <v-row>
        <v-col cols="12" sm="auto" md="6" class="d-flex flex-column justify-start centered-on-mobile">
          <h3 class="font-weight-bold">
            {{ translation(translationList.user_preferences, 'Preferințe cont') }}
          </h3>
          <p class="font-weight-regular">
            {{
              translation(translationList.preferences_helper, 'Aici puteți gestiona preferințele contului dumneavoastră.')
            }}
          </p>
        </v-col>
      </v-row>
      <div v-if="!showLoadingDialog">
        <v-row>
          <v-col cols="12">
            <customer-is-subscribed-to-newsletter
                v-model="userPreferenceForm.is_subscribed_to_newsletter"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <customer-has-rca-notification
                v-model="userPreferenceForm.has_rca_notification_enabled"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <customer-has-travel-insurance-notification
                v-model="userPreferenceForm.has_travel_insurance_notification_enabled"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <customer-has-house-insurance-notification
                v-model="userPreferenceForm.has_house_insurance_notification_enabled"/>
          </v-col>
        </v-row>
        <v-row class="mt-10 mb-15">
          <v-col cols="12" class="d-flex align-center justify-center">
            <v-btn
                class="block-on-mobile"
                color="primary"
                size="x-large"
                flat
                rounded="lg"
                @click="submitUserPreference"
            >{{ translation(translationList.save_changes, 'Salvează modificarile') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>
      <v-skeleton-loader v-else type="image" class="skeleton-h-250"></v-skeleton-loader>
    </v-card>
  </account-layout>
</template>

<style scoped>

</style>
